import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../i18n'
import { setLanguage, LANGUAGES } from '../i18n'
import { useAuth } from '../context'
import OfflineBanner from '../components/OfflineBanner'
import { PortalPage } from '../components/portal/PortalPage'
import { TRANSITION_PRESS, TRANSITION_UI } from '../lib/motion'

const ACCENT = '#e66e50'

const s: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    background: '#0a0f1a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logo: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: `linear-gradient(135deg, ${ACCENT}, #efb776)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
  },
  greeting: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '55%',
  },
  roleBadge: {
    padding: '2px 8px',
    borderRadius: 6,
    background: `rgba(230, 110, 80, 0.15)`,
    border: `1px solid rgba(230, 110, 80, 0.3)`,
    color: ACCENT,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  actions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: 10,
    border: 'none',
    background: 'rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.6)',
    position: 'relative' as const,
    transition: TRANSITION_PRESS,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    overflowY: 'auto' as const,
  },
  bottomBar: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    minHeight: 68,
    background: '#0d1320',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '0 8px',
    zIndex: 50,
  },
  tab: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 3,
    padding: '8px 16px',
    borderRadius: 12,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: TRANSITION_UI,
    minWidth: 60,
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.3,
  },
  langDropdown: {
    position: 'absolute' as const,
    top: 48,
    right: 0,
    background: '#151c2c',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 6,
    zIndex: 100,
    minWidth: 150,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  langItem: {
    padding: '10px 14px',
    borderRadius: 8,
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left' as const,
    display: 'block',
  },
  langItemActive: {
    background: `rgba(230, 110, 80, 0.15)`,
    color: ACCENT,
  },
}

const HomeIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? ACCENT : 'none'} stroke={active ? ACCENT : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
)

const QueueIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? ACCENT : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 14l2 2 4-4" />
  </svg>
)

const HistoryIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? ACCENT : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
)

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

const tabs = [
  { key: 'home', path: '/inspector/home', label: 'Home', Icon: HomeIcon },
  { key: 'queue', path: '/inspector/queue', label: 'Queue', Icon: QueueIcon },
  { key: 'history', path: '/inspector/history', label: 'History', Icon: HistoryIcon },
]

export function InspectorApp({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation()
  const { profile, logout, isImpersonating, exitImpersonation } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showLang, setShowLang] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowLang(false)
      }
    }
    if (showLang) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showLang])

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const handleExitViewAs = () => {
    exitImpersonation()
    navigate('/users')
  }

  const handleLangChange = (code: string) => {
    void (async () => {
      await setLanguage(code)
      setShowLang(false)
    })()
  }

  const name = profile?.full_name?.split(' ')[0] || t('inspector', 'Inspector')

  const activeTab = tabs.find(tab => location.pathname.startsWith(tab.path))?.key
    || (location.pathname.startsWith('/inspector/review') ? 'queue' : 'home')

  return (
    <div className="portal-shell" style={s.shell}>
      <OfflineBanner />
      <div className="portal-app-header" style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.logo}>RS</div>
          <span style={s.greeting}>{name}</span>
          <span style={s.roleBadge}>Inspector</span>
          {isImpersonating && (
            <span style={{ ...s.roleBadge, background: 'rgba(239, 183, 118, 0.2)', color: '#efb776' }}>
              Viewing As
            </span>
          )}
        </div>
        <div style={s.actions}>
          {isImpersonating && (
            <button
              type="button"
              style={{ ...s.iconBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 600 }}
              onClick={handleExitViewAs}
            >
              Exit View As
            </button>
          )}
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button style={s.iconBtn} onClick={() => setShowLang(!showLang)} aria-label={t('lang_select', 'Language')}>
              <GlobeIcon />
            </button>
            {showLang && (
              <div style={s.langDropdown}>
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    style={{
                      ...s.langItem,
                      ...(i18n.language === lang.code ? s.langItemActive : {}),
                    }}
                    onClick={() => handleLangChange(lang.code)}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button style={s.iconBtn} onClick={handleLogout} aria-label={t('home_logout', 'Logout')}>
            <LogoutIcon />
          </button>
        </div>
      </div>

      <div className="portal-content" style={s.content}>
        <PortalPage>{children}</PortalPage>
      </div>

      <div className="portal-tab-bar" style={s.bottomBar}>
        {tabs.map(({ key, path, label, Icon }) => {
          const isActive = activeTab === key
          return (
            <button
              key={key}
              style={{
                ...s.tab,
                background: isActive ? 'rgba(230, 110, 80, 0.1)' : 'transparent',
              }}
              onClick={() => navigate(path)}
            >
              <Icon active={isActive} />
              <span style={{
                ...s.tabLabel,
                color: isActive ? ACCENT : 'rgba(255,255,255,0.35)',
              }}>
                {label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
